import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import {assert} from 'Asserts';
import {SequenceAnalysis} from 'constants/SequenceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelectorView, {FolderSelectorViewOptions} from 'file-browser/FolderSelectorView';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import DuplicateModalTemplate from 'molbio/components/DuplicateModalTemplate';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import notifications from 'notifications';
import {withLoadingScreen} from 'util/Promises';

export interface DuplicateModalOptions extends Backbone.ViewOptions<Backbone.Model> {
  analyses: Array<SequenceAnalysis>;
  cloneSuccess: (res: {id: string}) => void;
  initialFolderId?: string | number | null;
  name: string;
  sequenceID: string;
  version?: number | string;
}

/**
 * @deprecated New UIs should rely on DuplicatePolymerModal.
 */
class DuplicateModal extends Backbone.View<Backbone.Model> {
  template!: typeof DuplicateModalTemplate;
  analyses!: Array<SequenceAnalysis>;
  cloneSuccess!: (res: {id: string}) => void;
  initialFolderId: string | number | null | undefined;
  name!: string;
  sequenceID!: string;
  version!: number | string;
  selectorView: FolderSelectorView | undefined;

  constructor(options: DuplicateModalOptions) {
    super(options);
  }

  static initClass(): void {
    this.prototype.template = DuplicateModalTemplate;
  }

  override events(): Backbone.EventsHash {
    return {
      'change .select-all': 'selectAllToggle',
      'change .copy-list input': 'renderSelectAll',
      'submit form': 'handleFormSubmit',
    };
  }

  override initialize({
    analyses,
    cloneSuccess,
    initialFolderId,
    name,
    sequenceID,
    version,
  }: DuplicateModalOptions): void {
    this.analyses = analyses;
    this.cloneSuccess = cloneSuccess;
    this.initialFolderId = initialFolderId;
    this.name = name;
    this.sequenceID = sequenceID;
    this.version = version ?? 'latest';
    this.clone = this.clone.bind(this);
    if (this.initialFolderId == null) {
      this.initialFolderId = FolderDefaults.get(FolderDefaultsKey.INVENTORY);
    }
  }

  override remove(): this {
    super.remove();
    if (this.selectorView != null) {
      this.selectorView.remove();
    }
    return this;
  }

  selectAllToggle(e: JQueryEventObject): void {
    const $target = $(e.currentTarget);
    const $list = $target.closest('div').find('.copy-list');
    $list.find('input').prop({
      checked: $target.prop('checked'),
    });
  }

  handleFormSubmit(e: JQueryEventObject): void {
    e.preventDefault();
    this.clone();
  }

  renderSelectAll(e: JQueryEventObject): void {
    const $list = $(e.currentTarget).closest('ul');
    const $wrapper = $list.closest('div');
    const total = $list.find('input').length;
    const checked = $list.find('input:checked').length;
    const all = checked === total;
    const some = checked > 0;
    $wrapper.find('.select-all').prop({
      checked: some,
      indeterminate: !all && some,
    });
  }

  renderFolderSelector(selectedFolderId: string | number | null | undefined): void {
    if (this.selectorView != null) {
      this.selectorView.remove();
    }
    const folderSelectorViewOptions: FolderSelectorViewOptions = {
      initialFolderId: selectedFolderId,
      inputClassName: 'folder-select',
      props: {showInventory: true},
    };
    this.selectorView = new FolderSelectorView(folderSelectorViewOptions);
    this.$('.folder-selector-container').html(this.selectorView.render().el);
  }

  clone(): void {
    const name = this.$el.find('.name-input').val() as string;
    if (!name) {
      notifications.error('Please enter a valid name.');
      return;
    }

    const destFolderId = this.$el.find('.folder-select').val() as string;
    if (!destFolderId) {
      notifications.error('Please select a folder.');
      return;
    }

    FolderDefaults.set(FolderDefaultsKey.INVENTORY, destFolderId);
    const copiedAnalyses = this.$el
      .find('input.copied-analysis:checked')
      .map(function (this: HTMLInputElement): number {
        return parseInt($(this).val() as string, 10);
      })
      .get() as unknown as Array<number>;

    withLoadingScreen(
      // The backend accepts the sentinel value 'latest' in addition to numeric version ids, even though
      // SequenceDAO.cloneVersion's type only declares `number`.
      SequenceDAO.cloneVersion(this.sequenceID, this.version as number, destFolderId, name, copiedAnalyses)
        .then((res) => {
          FolderDAOCache.invalidateFolderContents(destFolderId);
          this.cloneSuccess(res);
        })
        .catch(notifications.notifyError),
      'Loading...'
    );
  }

  override render(): this {
    // check for any unsaved analyses
    this.analyses.forEach((analysis) => {
      assert(_.isNumber(analysis.dbId), 'Analysis must have a numeric id');
    });
    this.$el.html(
      this.template({
        name: this.name,
        analyses: this.analyses,
      })
    );
    this.renderFolderSelector(this.initialFolderId);
    return this;
  }
}
DuplicateModal.initClass();

export default DuplicateModal;
